import React from "react";
import { CSSProperties } from "react";

// CSS for icons
const iconStyles: { [key: string]: CSSProperties } = {
  pencil: {
    display: "inline-block",
    width: "16px",
    height: "16px",
    position: "relative",
    cursor: "pointer",
  },
  check: {
    display: "inline-block",
    width: "18px",
    height: "18px",
    position: "relative",
    cursor: "pointer",
  },
  cross: {
    display: "inline-block",
    width: "18px",
    height: "18px",
    position: "relative",
    cursor: "pointer",
  },
};

interface WalletNicknameEditorProps {
  initialNickname: string;
  walletAddress: string;
  format: string;
  status: string;
  memo: boolean;
  currencyCode: string;
  onSave?: (
    walletAddress: string,
    format: string,
    status: string,
    memo: boolean,
    currencyCode: string,
    nickname: string,
  ) => void;
}

const WalletNicknameEditor = (props: WalletNicknameEditorProps) => {
  const { initialNickname, walletAddress, format, status, memo, currencyCode, onSave } = props;
  const [isEditing, setIsEditing] = React.useState(false);
  const [nickname, setNickname] = React.useState(initialNickname);
  const [tempNickname, setTempNickname] = React.useState(initialNickname);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Focus input when entering edit mode
  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleEditClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Prevent row selection
    setTempNickname(nickname);
    setIsEditing(true);
  };

  const handleSaveClick = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLInputElement>,
  ) => {
    e.stopPropagation(); // Prevent row selection
    setNickname(tempNickname);
    setIsEditing(false);
    onSave && onSave(walletAddress, format, status, memo, currencyCode, tempNickname);
  };

  const handleCancelClick = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLInputElement>,
  ) => {
    e.stopPropagation(); // Prevent row selection
    setTempNickname(nickname);
    setIsEditing(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Only allow alphanumeric characters (letters and numbers)
    const alphanumericValue = newValue.replace(/[^a-zA-Z0-9\s]/g, "");

    // Limit to 25 characters
    const validatedValue = alphanumericValue.slice(0, 25);

    // Update state with the validated value
    setTempNickname(validatedValue);

    // If validation changed the input, update the input field value
    if (newValue !== validatedValue) {
      e.target.value = validatedValue;
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSaveClick(e);
    } else if (e.key === "Escape") {
      handleCancelClick(e);
    }
  };

  // Pencil Icon Component
  const PencilIcon = () => (
    <div
      onClick={handleEditClick}
      style={iconStyles.pencil}
      title="Edit wallet nickname"
      className="pencil-icon">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
      </svg>
    </div>
  );

  // Check Icon Component
  const CheckIcon = () => (
    <div onClick={handleSaveClick} style={iconStyles.check} title="Save" className="check-icon">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
    </div>
  );

  // Cross Icon Component
  const CrossIcon = () => (
    <div onClick={handleCancelClick} style={iconStyles.cross} title="Cancel" className="cross-icon">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </div>
  );

  return (
    <div className="wallet-nickname-editor" style={{ display: "flex", alignItems: "center" }}>
      {isEditing ? (
        <>
          <input
            ref={inputRef}
            type="text"
            value={tempNickname}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="nickname-input"
            style={{
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "4px 8px",
              marginRight: "8px",
              width: "128px",
              outline: "none",
            }}
            autoComplete="off"
          />
          <div style={{ display: "flex", gap: "4px" }}>
            <div style={{ color: "#22c55e" }}>
              <CheckIcon />
            </div>
            <div style={{ color: "#ef4444" }}>
              <CrossIcon />
            </div>
          </div>
        </>
      ) : (
        <>
          <span
            className="nickname-display"
            style={{
              marginRight: "8px",
              fontWeight: "500",
              color: "#111827",
            }}>
            {nickname}
          </span>
          <div style={{ color: "#6b7280" }}>
            <PencilIcon />
          </div>
        </>
      )}
    </div>
  );
};

export default WalletNicknameEditor;
